import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import SpinnerOverlay from '../../components/ui/SpinnerOverlay';
import SuccessToast from '../../components/ui/SuccessToast';

const statusConfig = {
  draft: { label: 'Draft', color: '#94a3b8', bg: 'rgba(148,163,184,0.12)', icon: 'FileText' },
  pending_review: { label: 'Under Review', color: '#fbbf24', bg: 'rgba(251,191,36,0.12)', icon: 'Clock' },
  active: { label: 'Active', color: '#34d399', bg: 'rgba(52,211,153,0.12)', icon: 'CheckCircle' },
  suspended: { label: 'Suspended', color: '#f87171', bg: 'rgba(248,113,113,0.12)', icon: 'AlertCircle' },
};

const bookingStatusColors = {
  requested: '#fbbf24', approved: '#34d399', active: '#ffd700',
  completed: '#a78bfa', cancelled: '#f87171',
};

const StarRating = ({ rating, size = 14 }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5]?.map(s => (
      <svg key={s} width={size} height={size} viewBox="0 0 24 24" fill={s <= rating ? '#ffd700' : 'rgba(255,255,255,0.15)'}>
        <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
      </svg>
    ))}
  </div>
);

const VehicleThumbnail = ({ media, size = 'md' }) => {
  const [imgUrl, setImgUrl] = useState(null);
  const primaryMedia = media?.find(m => m?.is_primary) || media?.[0];
  const dims = size === 'sm' ? 'w-14 h-12' : 'w-20 h-16';

  useEffect(() => {
    if (primaryMedia?.storage_path) {
      supabase?.storage?.from('vehicle-media')?.createSignedUrl(primaryMedia?.storage_path, 3600)
        ?.then(({ data }) => { if (data?.signedUrl) setImgUrl(data?.signedUrl); });
    }
  }, [primaryMedia?.storage_path]);

  if (imgUrl) {
    return (
      <div className={`${dims} rounded-xl overflow-hidden flex-shrink-0`}>
        <img src={imgUrl} alt="Vehicle" className="w-full h-full object-cover" />
      </div>
    );
  }
  return (
    <div className={`${dims} rounded-xl bg-white/10 flex items-center justify-center text-2xl flex-shrink-0`}>🚗</div>
  );
};

// ─── Vehicle Edit Modal ────────────────────────────────────────────────────────
const VehicleEditModal = ({ vehicle, onClose, onSaved }) => {
  const [form, setForm] = useState({
    title: vehicle?.title || '',
    description: vehicle?.description || '',
    location_address: vehicle?.location_address || '',
    year: vehicle?.year || '',
    color: vehicle?.color || '',
    seats: vehicle?.seats || '',
    transmission: vehicle?.transmission || 'automatic',
    fuel_type: vehicle?.fuel_type || 'petrol',
  });
  const [pricing, setPricing] = useState({
    base_daily_rate: vehicle?.vehicle_pricing?.[0]?.base_daily_rate || '',
    weekly_rate: vehicle?.vehicle_pricing?.[0]?.weekly_rate || '',
    monthly_rate: vehicle?.vehicle_pricing?.[0]?.monthly_rate || '',
    security_deposit: vehicle?.vehicle_pricing?.[0]?.security_deposit || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const { error: vErr } = await supabase?.from('vehicles')?.update({
        title: form?.title,
        description: form?.description,
        location_address: form?.location_address,
        year: form?.year ? parseInt(form?.year) : null,
        color: form?.color,
        seats: form?.seats ? parseInt(form?.seats) : null,
        transmission: form?.transmission,
        fuel_type: form?.fuel_type,
        updated_at: new Date()?.toISOString(),
      })?.eq('id', vehicle?.id);
      if (vErr) throw vErr;

      if (vehicle?.vehicle_pricing?.[0]?.id) {
        const { error: pErr } = await supabase?.from('vehicle_pricing')?.update({
          base_daily_rate: pricing?.base_daily_rate ? parseFloat(pricing?.base_daily_rate) : null,
          weekly_rate: pricing?.weekly_rate ? parseFloat(pricing?.weekly_rate) : null,
          monthly_rate: pricing?.monthly_rate ? parseFloat(pricing?.monthly_rate) : null,
          security_deposit: pricing?.security_deposit ? parseFloat(pricing?.security_deposit) : null,
        })?.eq('id', vehicle?.vehicle_pricing?.[0]?.id);
        if (pErr) throw pErr;
      }
      onSaved?.();
    } catch (e) {
      setError(e?.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 12,
    color: '#fff',
    padding: '10px 14px',
    fontSize: 14,
    width: '100%',
    outline: 'none',
  };

  const labelStyle = { color: 'rgba(255,255,255,0.5)', fontSize: 12, marginBottom: 4, display: 'block' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(10px)' }}>
      <div className="w-full max-w-lg rounded-2xl overflow-hidden" style={{ background: '#0f1f38', border: '1px solid rgba(255,255,255,0.12)', maxHeight: '90vh', overflowY: 'auto' }}>
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-white/10" style={{ background: '#0f1f38' }}>
          <div>
            <h3 className="text-white font-bold text-lg">Edit Vehicle</h3>
            <p className="text-white/40 text-xs mt-0.5 truncate max-w-xs">{vehicle?.title}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-white/10 transition-colors">
            <Icon name="X" size={18} className="text-white/60" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Basic Info */}
          <div>
            <p className="text-white/30 text-xs font-bold uppercase tracking-wider mb-3">Basic Info</p>
            <div className="space-y-3">
              <div>
                <label style={labelStyle}>Listing Title</label>
                <input style={inputStyle} value={form?.title} onChange={e => setForm(p => ({ ...p, title: e?.target?.value }))} placeholder="e.g. 2022 Toyota Prado – Nairobi" />
              </div>
              <div>
                <label style={labelStyle}>Description</label>
                <textarea style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} value={form?.description} onChange={e => setForm(p => ({ ...p, description: e?.target?.value }))} placeholder="Describe your vehicle..." />
              </div>
              <div>
                <label style={labelStyle}>Pickup Location</label>
                <input style={inputStyle} value={form?.location_address} onChange={e => setForm(p => ({ ...p, location_address: e?.target?.value }))} placeholder="e.g. Westlands, Nairobi" />
              </div>
            </div>
          </div>

          {/* Vehicle Specs */}
          <div>
            <p className="text-white/30 text-xs font-bold uppercase tracking-wider mb-3">Vehicle Specs</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label style={labelStyle}>Year</label>
                <input style={inputStyle} type="number" value={form?.year} onChange={e => setForm(p => ({ ...p, year: e?.target?.value }))} placeholder="2022" />
              </div>
              <div>
                <label style={labelStyle}>Color</label>
                <input style={inputStyle} value={form?.color} onChange={e => setForm(p => ({ ...p, color: e?.target?.value }))} placeholder="e.g. White" />
              </div>
              <div>
                <label style={labelStyle}>Seats</label>
                <input style={inputStyle} type="number" value={form?.seats} onChange={e => setForm(p => ({ ...p, seats: e?.target?.value }))} placeholder="5" />
              </div>
              <div>
                <label style={labelStyle}>Transmission</label>
                <select style={inputStyle} value={form?.transmission} onChange={e => setForm(p => ({ ...p, transmission: e?.target?.value }))}>
                  <option value="automatic">Automatic</option>
                  <option value="manual">Manual</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Fuel Type</label>
                <select style={inputStyle} value={form?.fuel_type} onChange={e => setForm(p => ({ ...p, fuel_type: e?.target?.value }))}>
                  <option value="petrol">Petrol</option>
                  <option value="diesel">Diesel</option>
                  <option value="electric">Electric</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div>
            <p className="text-white/30 text-xs font-bold uppercase tracking-wider mb-3">Pricing (KES)</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: 'base_daily_rate', label: 'Daily Rate' },
                { key: 'weekly_rate', label: 'Weekly Rate' },
                { key: 'monthly_rate', label: 'Monthly Rate' },
                { key: 'security_deposit', label: 'Security Deposit' },
              ]?.map(({ key, label }) => (
                <div key={key}>
                  <label style={labelStyle}>{label}</label>
                  <input style={inputStyle} type="number" value={pricing?.[key]} onChange={e => setPricing(p => ({ ...p, [key]: e?.target?.value }))} placeholder="0" />
                </div>
              ))}
            </div>
          </div>

          {error && (
            <div className="rounded-xl p-3 text-sm" style={{ background: 'rgba(248,113,113,0.1)', color: '#f87171', border: '1px solid rgba(248,113,113,0.25)' }}>
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 py-3 rounded-xl text-sm font-semibold text-white/60 hover:bg-white/10 transition-colors border border-white/10">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving} className="flex-1 py-3 rounded-xl text-sm font-bold transition-all disabled:opacity-60" style={{ background: '#ffd700', color: '#0a1628' }}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Host Dashboard ───────────────────────────────────────────────────────
const HostDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [vehicles, setVehicles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [editVehicle, setEditVehicle] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [bookingTab, setBookingTab] = useState('requested');

  useEffect(() => {
    if (user) fetchAll();
  }, [user]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [vehiclesRes, bookingsRes, reviewsRes] = await Promise.all([
        supabase?.from('vehicles')
          ?.select('*, vehicle_pricing(*), vehicle_media(*)')
          ?.eq('owner_id', user?.id)
          ?.order('created_at', { ascending: false }),
        supabase?.from('rental_bookings')
          ?.select('*, vehicles(title, make, model, vehicle_media(*)), user_profiles!rental_bookings_customer_id_fkey(full_name, phone, email)')
          ?.eq('owner_id', user?.id)
          ?.order('created_at', { ascending: false }),
        supabase?.from('rental_bookings')
          ?.select('id, booking_number, rating, review_comment, reviewed_at, start_at, end_at, vehicles(title, make, model), user_profiles!rental_bookings_customer_id_fkey(full_name)')
          ?.eq('owner_id', user?.id)
          ?.not('rating', 'is', null)
          ?.order('reviewed_at', { ascending: false }),
      ]);
      setVehicles(vehiclesRes?.data || []);
      setBookings(bookingsRes?.data || []);
      setReviews(reviewsRes?.data || []);
    } catch (e) {
      if (e?.message?.includes('AbortError') || e?.message?.includes('Lock broken')) {
        setTimeout(() => fetchAll(), 1000);
        return;
      }
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (bookingId, accept) => {
    setProcessing(bookingId);
    try {
      const { data, error } = await supabase?.rpc('owner_respond_to_booking', {
        p_booking_id: bookingId,
        p_owner_id: user?.id,
        p_accept: accept,
      });
      if (error) throw error;
      if (data?.success) {
        setSuccessMsg(accept ? 'Booking accepted!' : 'Booking declined');
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2500);
        fetchAll();
      }
    } catch (e) { console.error(e); }
    finally { setProcessing(null); }
  };

  // ─── Computed Stats ─────────────────────────────────────────────────────────
  const completedBookings = bookings?.filter(b => b?.booking_status === 'completed');
  const totalEarnings = completedBookings?.reduce((sum, b) => sum + ((b?.price_breakdown?.total || 0) * 0.9), 0);
  const activeBookings = bookings?.filter(b => b?.booking_status === 'active');
  const pendingRequests = bookings?.filter(b => b?.booking_status === 'requested');
  const activeVehicles = vehicles?.filter(v => v?.status === 'active');
  const avgRating = reviews?.length > 0 ? (reviews?.reduce((s, r) => s + (r?.rating || 0), 0) / reviews?.length) : 0;

  // Monthly earnings (last 6 months)
  const monthlyEarnings = (() => {
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date();
      d?.setMonth(d?.getMonth() - i);
      const label = d?.toLocaleDateString('en-KE', { month: 'short' });
      const monthStr = d?.toISOString()?.slice(0, 7);
      const earned = completedBookings
        ?.filter(b => b?.end_at?.startsWith(monthStr))
        ?.reduce((s, b) => s + ((b?.price_breakdown?.total || 0) * 0.9), 0);
      months?.push({ label, earned });
    }
    return months;
  })();
  const maxEarned = Math.max(...monthlyEarnings?.map(m => m?.earned), 1);

  const formatDate = (d) => d ? new Date(d)?.toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
  const formatCurrency = (n) => `KES ${(n || 0)?.toLocaleString('en-KE', { maximumFractionDigits: 0 })}`;

  const tabs = [
    { key: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { key: 'vehicles', label: 'My Vehicles', icon: 'Car' },
    { key: 'bookings', label: 'Bookings', icon: 'Calendar', badge: pendingRequests?.length },
    { key: 'earnings', label: 'Earnings', icon: 'TrendingUp' },
    { key: 'reviews', label: 'Reviews', icon: 'Star' },
  ];

  const bookingTabs = [
    { key: 'requested', label: 'Pending' },
    { key: 'approved', label: 'Approved' },
    { key: 'active', label: 'Active' },
    { key: 'completed', label: 'Completed' },
    { key: 'cancelled', label: 'Cancelled' },
  ];

  const filteredBookings = bookings?.filter(b => b?.booking_status === bookingTab);

  return (
    <div className="min-h-screen" style={{ background: '#0a1628', fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');`}</style>
      {processing && <SpinnerOverlay message="Processing..." />}
      {showSuccess && <SuccessToast message={successMsg} visible={showSuccess} subMessage="" onDismiss={() => setShowSuccess(false)} />}
      {editVehicle && (
        <VehicleEditModal
          vehicle={editVehicle}
          onClose={() => setEditVehicle(null)}
          onSaved={() => {
            setEditVehicle(null);
            setSuccessMsg('Vehicle updated successfully!');
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 2500);
            fetchAll();
          }}
        />
      )}
      {/* Header */}
      <div className="sticky top-0 z-40 border-b border-white/10" style={{ background: 'rgba(10,22,40,0.97)', backdropFilter: 'blur(20px)' }}>
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/')} className="p-2 rounded-xl hover:bg-white/10 transition-colors">
              <Icon name="ArrowLeft" size={20} className="text-white" />
            </button>
            <div>
              <h1 className="text-white font-black text-xl">Host Dashboard</h1>
              <p className="text-white/40 text-xs">Manage your fleet & earnings</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {pendingRequests?.length > 0 && (
              <button onClick={() => { setActiveTab('bookings'); setBookingTab('requested'); }} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold animate-pulse" style={{ background: 'rgba(251,191,36,0.15)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)' }}>
                <Icon name="Bell" size={13} />
                {pendingRequests?.length} new
              </button>
            )}
            <button onClick={() => navigate('/list-my-car')} className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold transition-all hover:opacity-90" style={{ background: '#ffd700', color: '#0a1628' }}>
              <Icon name="Plus" size={15} />
              Add Vehicle
            </button>
          </div>
        </div>

        {/* Tab Bar */}
        <div className="max-w-6xl mx-auto px-4 flex gap-1 overflow-x-auto pb-0 scrollbar-hide">
          {tabs?.map(tab => (
            <button
              key={tab?.key}
              onClick={() => setActiveTab(tab?.key)}
              className="flex items-center gap-1.5 px-4 py-3 text-sm font-semibold whitespace-nowrap transition-all relative flex-shrink-0"
              style={{ color: activeTab === tab?.key ? '#ffd700' : 'rgba(255,255,255,0.45)', borderBottom: activeTab === tab?.key ? '2px solid #ffd700' : '2px solid transparent' }}
            >
              <Icon name={tab?.icon} size={15} />
              {tab?.label}
              {tab?.badge > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-xs font-black" style={{ background: '#fbbf24', color: '#0a1628', fontSize: 10 }}>
                  {tab?.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            {[1, 2, 3, 4]?.map(i => <div key={i} className="h-28 rounded-2xl animate-pulse" style={{ background: 'rgba(255,255,255,0.05)' }} />)}
          </div>
        ) : (
          <>
            {/* ─── OVERVIEW TAB ─────────────────────────────────────────────────── */}
            {activeTab === 'overview' && (
              <div className="space-y-8">
                {/* KPI Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { label: 'Total Earnings', value: formatCurrency(totalEarnings), icon: 'DollarSign', color: '#ffd700', sub: `${completedBookings?.length} completed trips` },
                    { label: 'Active Vehicles', value: activeVehicles?.length, icon: 'Car', color: '#34d399', sub: `${vehicles?.length} total listed` },
                    { label: 'Active Bookings', value: activeBookings?.length, icon: 'Calendar', color: '#60a5fa', sub: `${pendingRequests?.length} pending requests` },
                    { label: 'Avg Rating', value: avgRating > 0 ? avgRating?.toFixed(1) : '—', icon: 'Star', color: '#a78bfa', sub: `${reviews?.length} reviews` },
                  ]?.map(kpi => (
                    <div key={kpi?.label} className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-white/40 text-xs font-semibold">{kpi?.label}</span>
                        <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: `${kpi?.color}18` }}>
                          <Icon name={kpi?.icon} size={16} style={{ color: kpi?.color }} />
                        </div>
                      </div>
                      <div className="text-white font-black text-2xl mb-1">{kpi?.value}</div>
                      <div className="text-white/30 text-xs">{kpi?.sub}</div>
                    </div>
                  ))}
                </div>

                {/* Quick Actions */}
                <div>
                  <h2 className="text-white font-bold text-base mb-4">Quick Actions</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: 'View Bookings', icon: 'Calendar', color: '#fbbf24', action: () => setActiveTab('bookings') },
                      { label: 'My Vehicles', icon: 'Car', color: '#34d399', action: () => setActiveTab('vehicles') },
                      { label: 'Earnings', icon: 'TrendingUp', color: '#60a5fa', action: () => setActiveTab('earnings') },
                      { label: 'List New Car', icon: 'Plus', color: '#ffd700', action: () => navigate('/list-my-car') },
                    ]?.map(qa => (
                      <button key={qa?.label} onClick={qa?.action} className="rounded-2xl p-4 text-left transition-all hover:scale-[1.02]" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${qa?.color}18` }}>
                          <Icon name={qa?.icon} size={18} style={{ color: qa?.color }} />
                        </div>
                        <span className="text-white font-semibold text-sm">{qa?.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Recent Bookings */}
                {bookings?.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-white font-bold text-base">Recent Bookings</h2>
                      <button onClick={() => setActiveTab('bookings')} className="text-xs font-semibold" style={{ color: '#ffd700' }}>View all →</button>
                    </div>
                    <div className="space-y-3">
                      {bookings?.slice(0, 4)?.map(booking => {
                        const sc = bookingStatusColors?.[booking?.booking_status] || '#94a3b8';
                        return (
                          <div key={booking?.id} className="flex items-center gap-4 rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                            <VehicleThumbnail media={booking?.vehicles?.vehicle_media || []} size="sm" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-white font-semibold text-sm truncate">{booking?.vehicles?.title || `${booking?.vehicles?.make} ${booking?.vehicles?.model}`}</span>
                                <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-bold capitalize" style={{ background: `${sc}18`, color: sc }}>
                                  {booking?.booking_status}
                                </span>
                              </div>
                              <div className="flex items-center gap-3 mt-1 text-xs text-white/40">
                                <span>{booking?.user_profiles?.full_name || 'Customer'}</span>
                                <span>·</span>
                                <span>{formatDate(booking?.start_at)}</span>
                                <span>·</span>
                                <span className="font-semibold" style={{ color: '#ffd700' }}>{formatCurrency(booking?.price_breakdown?.total)}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Recent Reviews */}
                {reviews?.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-white font-bold text-base">Recent Reviews</h2>
                      <button onClick={() => setActiveTab('reviews')} className="text-xs font-semibold" style={{ color: '#ffd700' }}>View all →</button>
                    </div>
                    <div className="space-y-3">
                      {reviews?.slice(0, 3)?.map(review => (
                        <div key={review?.id} className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                          <div className="flex items-start gap-3">
                            <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold" style={{ background: 'rgba(255,215,0,0.15)', color: '#ffd700' }}>
                              {review?.user_profiles?.full_name?.[0]?.toUpperCase() || '?'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <span className="text-white font-semibold text-sm">{review?.user_profiles?.full_name || 'Renter'}</span>
                                <StarRating rating={review?.rating} />
                              </div>
                              <p className="text-white/50 text-xs">{review?.vehicles?.title}</p>
                              {review?.review_comment && <p className="text-white/70 text-sm mt-2 leading-relaxed">"{review?.review_comment}"</p>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ─── VEHICLES TAB ─────────────────────────────────────────────────── */}
            {activeTab === 'vehicles' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-white font-bold text-lg">My Vehicles <span className="text-white/30 font-normal text-base">({vehicles?.length})</span></h2>
                  <button onClick={() => navigate('/list-my-car')} className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold" style={{ background: '#ffd700', color: '#0a1628' }}>
                    <Icon name="Plus" size={15} /> Add Vehicle
                  </button>
                </div>

                {vehicles?.length === 0 ? (
                  <div className="text-center py-24">
                    <div className="text-6xl mb-4">🚗</div>
                    <h3 className="text-white font-bold text-xl mb-2">No vehicles listed yet</h3>
                    <p className="text-white/40 text-sm mb-6">Start earning by listing your first vehicle</p>
                    <button onClick={() => navigate('/list-my-car')} className="px-6 py-3 rounded-xl font-bold text-sm" style={{ background: '#ffd700', color: '#0a1628' }}>List Your Car</button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {vehicles?.map(vehicle => {
                      const status = statusConfig?.[vehicle?.status] || statusConfig?.draft;
                      const pricing = vehicle?.vehicle_pricing?.[0];
                      const vBookings = bookings?.filter(b => b?.vehicle_id === vehicle?.id);
                      const vCompleted = vBookings?.filter(b => b?.booking_status === 'completed');
                      const vEarnings = vCompleted?.reduce((s, b) => s + ((b?.price_breakdown?.total || 0) * 0.9), 0);

                      return (
                        <div key={vehicle?.id} className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                          <div className="flex items-start gap-4">
                            <VehicleThumbnail media={vehicle?.vehicle_media || []} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <h3 className="text-white font-bold text-base truncate">{vehicle?.title}</h3>
                                <span className="flex-shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: status?.bg, color: status?.color }}>
                                  <Icon name={status?.icon} size={11} />
                                  {status?.label}
                                </span>
                              </div>
                              <p className="text-white/40 text-xs mb-3">{vehicle?.location_address} · {vehicle?.make} {vehicle?.model} {vehicle?.year}</p>
                              <div className="flex flex-wrap gap-4 text-sm">
                                <div><span className="text-white/40 text-xs block">Daily Rate</span><span className="text-[#ffd700] font-bold">{pricing?.base_daily_rate ? `KES ${pricing?.base_daily_rate?.toLocaleString()}` : '—'}</span></div>
                                <div><span className="text-white/40 text-xs block">Bookings</span><span className="text-white font-bold">{vBookings?.length}</span></div>
                                <div><span className="text-white/40 text-xs block">Earnings</span><span className="text-[#34d399] font-bold">{formatCurrency(vEarnings)}</span></div>
                              </div>
                            </div>
                          </div>
                          <div className="mt-4 pt-4 border-t border-white/8 flex flex-wrap gap-2">
                            <button onClick={() => setEditVehicle(vehicle)} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all hover:opacity-90" style={{ background: 'rgba(96,165,250,0.12)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.25)' }}>
                              <Icon name="Edit2" size={13} /> Edit Details
                            </button>
                            {vehicle?.status === 'active' && (
                              <button onClick={() => { setActiveTab('bookings'); }} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all hover:opacity-90" style={{ background: 'rgba(255,215,0,0.1)', color: '#ffd700', border: '1px solid rgba(255,215,0,0.2)' }}>
                                <Icon name="Calendar" size={13} /> View Bookings
                              </button>
                            )}
                            <button onClick={() => navigate(`/car-detail?id=${vehicle?.id}`)} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all hover:opacity-90" style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.1)' }}>
                              <Icon name="Eye" size={13} /> Preview
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ─── BOOKINGS TAB ─────────────────────────────────────────────────── */}
            {activeTab === 'bookings' && (
              <div>
                <h2 className="text-white font-bold text-lg mb-5">Booking Requests</h2>
                {/* Sub-tabs */}
                <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                  {bookingTabs?.map(tab => {
                    const count = bookings?.filter(b => b?.booking_status === tab?.key)?.length;
                    const sc = bookingStatusColors?.[tab?.key] || '#94a3b8';
                    return (
                      <button key={tab?.key} onClick={() => setBookingTab(tab?.key)}
                        className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-all"
                        style={{ background: bookingTab === tab?.key ? `${sc}18` : 'rgba(255,255,255,0.06)', color: bookingTab === tab?.key ? sc : 'rgba(255,255,255,0.5)', border: `1px solid ${bookingTab === tab?.key ? `${sc}35` : 'rgba(255,255,255,0.1)'}` }}>
                        {tab?.label}
                        {count > 0 && <span className="px-1.5 py-0.5 rounded-full text-xs font-black" style={{ background: bookingTab === tab?.key ? sc : 'rgba(255,255,255,0.15)', color: bookingTab === tab?.key ? '#0a1628' : 'rgba(255,255,255,0.6)', fontSize: 10 }}>{count}</span>}
                      </button>
                    );
                  })}
                </div>

                {filteredBookings?.length === 0 ? (
                  <div className="text-center py-20">
                    <div className="text-5xl mb-3">📋</div>
                    <h3 className="text-white font-bold text-lg mb-1">No {bookingTab} bookings</h3>
                    <p className="text-white/40 text-sm">Booking requests will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredBookings?.map(booking => {
                      const sc = bookingStatusColors?.[booking?.booking_status] || '#94a3b8';
                      const customer = booking?.user_profiles;
                      const breakdown = booking?.price_breakdown || {};
                      return (
                        <div key={booking?.id} className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                          <div className="flex items-start justify-between gap-3 mb-4">
                            <div className="flex items-start gap-3">
                              <VehicleThumbnail media={booking?.vehicles?.vehicle_media || []} size="sm" />
                              <div>
                                <h3 className="text-white font-bold text-sm">#{booking?.booking_number}</h3>
                                <p className="text-white/40 text-xs mt-0.5">{booking?.vehicles?.title || `${booking?.vehicles?.make} ${booking?.vehicles?.model}`}</p>
                              </div>
                            </div>
                            <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-bold capitalize" style={{ background: `${sc}18`, color: sc }}>
                              {booking?.booking_status}
                            </span>
                          </div>

                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 text-xs">
                            <div><div className="text-white/40 mb-0.5">Customer</div><div className="text-white font-semibold">{customer?.full_name || 'N/A'}</div></div>
                            <div><div className="text-white/40 mb-0.5">Dates</div><div className="text-white font-semibold">{formatDate(booking?.start_at)} → {formatDate(booking?.end_at)}</div></div>
                            <div><div className="text-white/40 mb-0.5">Mode</div><div className="text-white font-semibold capitalize">{booking?.rental_mode?.replace('_', ' ') || '—'}</div></div>
                            <div><div className="text-white/40 mb-0.5">Total</div><div className="font-bold" style={{ color: '#ffd700' }}>{formatCurrency(breakdown?.total)}</div></div>
                          </div>

                          {customer?.phone && (
                            <div className="flex items-center gap-2 text-xs text-white/40 mb-4">
                              <Icon name="Phone" size={12} />
                              <span>{customer?.phone}</span>
                            </div>
                          )}

                          {booking?.booking_status === 'requested' && (
                            <div className="flex gap-3">
                              <button onClick={() => handleRespond(booking?.id, true)} disabled={!!processing} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-bold transition-all disabled:opacity-60" style={{ background: '#34d399', color: '#0a1628' }}>
                                <Icon name="Check" size={15} /> Accept
                              </button>
                              <button onClick={() => handleRespond(booking?.id, false)} disabled={!!processing} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-bold transition-all disabled:opacity-60" style={{ background: 'rgba(248,113,113,0.15)', color: '#f87171', border: '1px solid rgba(248,113,113,0.3)' }}>
                                <Icon name="X" size={15} /> Decline
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ─── EARNINGS TAB ─────────────────────────────────────────────────── */}
            {activeTab === 'earnings' && (
              <div className="space-y-6">
                {/* Earnings Summary Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { label: 'Total Earnings', value: formatCurrency(totalEarnings), icon: 'DollarSign', color: '#ffd700', sub: 'After 10% platform fee' },
                    { label: 'Completed Trips', value: completedBookings?.length, icon: 'CheckCircle', color: '#34d399', sub: 'Paid out bookings' },
                    { label: 'Avg per Trip', value: completedBookings?.length > 0 ? formatCurrency(totalEarnings / completedBookings?.length) : 'KES 0', icon: 'TrendingUp', color: '#a78bfa', sub: 'Average booking value' },
                  ]?.map(card => (
                    <div key={card?.label} className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${card?.color}18` }}>
                          <Icon name={card?.icon} size={18} style={{ color: card?.color }} />
                        </div>
                        <span className="text-white/50 text-sm">{card?.label}</span>
                      </div>
                      <div className="text-white font-black text-2xl mb-1">{card?.value}</div>
                      <div className="text-white/30 text-xs">{card?.sub}</div>
                    </div>
                  ))}
                </div>

                {/* Monthly Earnings Chart */}
                <div className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <h3 className="text-white font-bold text-base mb-6">Monthly Earnings</h3>
                  <div className="flex items-end gap-3 h-40">
                    {monthlyEarnings?.map((m, i) => {
                      const pct = maxEarned > 0 ? (m?.earned / maxEarned) * 100 : 0;
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-2">
                          <span className="text-white/40 text-xs font-semibold">{m?.earned > 0 ? `${(m?.earned / 1000)?.toFixed(0)}k` : ''}</span>
                          <div className="w-full rounded-t-lg transition-all duration-700 relative group" style={{ height: `${Math.max(pct, 4)}%`, background: pct > 0 ? 'linear-gradient(to top, #ffd700, #ffed4a)' : 'rgba(255,255,255,0.08)' }}>
                            <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black/80 text-white text-xs px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                              {formatCurrency(m?.earned)}
                            </div>
                          </div>
                          <span className="text-white/40 text-xs">{m?.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Earnings Breakdown by Vehicle */}
                {vehicles?.filter(v => v?.status === 'active')?.length > 0 && (
                  <div className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <h3 className="text-white font-bold text-base mb-4">Earnings by Vehicle</h3>
                    <div className="space-y-3">
                      {vehicles?.map(vehicle => {
                        const vCompleted = bookings?.filter(b => b?.vehicle_id === vehicle?.id && b?.booking_status === 'completed');
                        const vEarnings = vCompleted?.reduce((s, b) => s + ((b?.price_breakdown?.total || 0) * 0.9), 0);
                        const pct = totalEarnings > 0 ? (vEarnings / totalEarnings) * 100 : 0;
                        return (
                          <div key={vehicle?.id}>
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-white/70 text-sm font-semibold truncate max-w-xs">{vehicle?.title}</span>
                              <span className="text-white font-bold text-sm ml-4">{formatCurrency(vEarnings)}</span>
                            </div>
                            <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: 'linear-gradient(to right, #ffd700, #ffed4a)' }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Recent Completed Bookings */}
                {completedBookings?.length > 0 && (
                  <div className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <h3 className="text-white font-bold text-base mb-4">Recent Payouts</h3>
                    <div className="space-y-3">
                      {completedBookings?.slice(0, 6)?.map(booking => (
                        <div key={booking?.id} className="flex items-center justify-between py-3 border-b border-white/6 last:border-0">
                          <div>
                            <p className="text-white font-semibold text-sm">{booking?.vehicles?.title || `${booking?.vehicles?.make} ${booking?.vehicles?.model}`}</p>
                            <p className="text-white/40 text-xs mt-0.5">{booking?.user_profiles?.full_name} · {formatDate(booking?.end_at)}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-sm" style={{ color: '#34d399' }}>+{formatCurrency((booking?.price_breakdown?.total || 0) * 0.9)}</p>
                            <p className="text-white/30 text-xs">After fee</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ─── REVIEWS TAB ─────────────────────────────────────────────────── */}
            {activeTab === 'reviews' && (
              <div className="space-y-6">
                {/* Rating Summary */}
                {reviews?.length > 0 && (
                  <div className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div className="flex items-center gap-8">
                      <div className="text-center">
                        <div className="text-white font-black text-5xl mb-1">{avgRating?.toFixed(1)}</div>
                        <StarRating rating={Math.round(avgRating)} size={18} />
                        <p className="text-white/40 text-xs mt-2">{reviews?.length} reviews</p>
                      </div>
                      <div className="flex-1 space-y-2">
                        {[5, 4, 3, 2, 1]?.map(star => {
                          const count = reviews?.filter(r => r?.rating === star)?.length;
                          const pct = reviews?.length > 0 ? (count / reviews?.length) * 100 : 0;
                          return (
                            <div key={star} className="flex items-center gap-3">
                              <span className="text-white/40 text-xs w-3">{star}</span>
                              <svg width={12} height={12} viewBox="0 0 24 24" fill="#ffd700"><polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" /></svg>
                              <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: '#ffd700' }} />
                              </div>
                              <span className="text-white/30 text-xs w-6 text-right">{count}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {reviews?.length === 0 ? (
                  <div className="text-center py-24">
                    <div className="text-6xl mb-4">⭐</div>
                    <h3 className="text-white font-bold text-xl mb-2">No reviews yet</h3>
                    <p className="text-white/40 text-sm">Reviews from renters will appear here after completed trips</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {reviews?.map(review => (
                      <div key={review?.id} className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm" style={{ background: 'rgba(255,215,0,0.15)', color: '#ffd700' }}>
                            {review?.user_profiles?.full_name?.[0]?.toUpperCase() || '?'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <span className="text-white font-bold text-sm">{review?.user_profiles?.full_name || 'Renter'}</span>
                              <StarRating rating={review?.rating} />
                            </div>
                            <p className="text-white/40 text-xs mb-2">{review?.vehicles?.title} · {formatDate(review?.reviewed_at || review?.end_at)}</p>
                            {review?.review_comment && (
                              <p className="text-white/70 text-sm leading-relaxed">"{review?.review_comment}"</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default HostDashboard;
